import { useMutation, useQueryClient } from 'react-query';
import * as Sentry from '@sentry/react';
import { Axios } from '@utils/api/axios-instance.ts';
import { PUT_URL } from '@utils/api/endpoints.ts';
import { UpdateDocumentMutationParams } from '@utils/types/editors/transcripts/api.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import { ValidationError } from 'yup';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import { DocumentResult } from '@utils/types/editors/generic.ts';
import toast from 'react-hot-toast';

export function useUpdateDocument() {
  const queryClient = useQueryClient();

  return useMutation(
    ['updateDocument'], // Mutation key for better error tracking
    async (data: UpdateDocumentMutationParams) => {
      const { sessionId, documentId } = data;
      return await Axios.put<DocumentResult>(
        PUT_URL(sessionId, documentId).updateSessionDocument,
        {
          filename: data.filename,
          document: data.document,
          timecode_offset: data.timecode_offset,
          plain_document_changed: data.plain_document_changed
        }
      );
    },
    {
      onMutate: async newData => {
        // Cancel outgoing refetches
        await queryClient.cancelQueries(
          QueryKeys.document(newData.sessionId, newData.documentId)
        );

        // Snapshot the previous value
        const previousDocument = queryClient.getQueryData(
          QueryKeys.document(newData.sessionId, newData.documentId)
        );

        // Optimistically update to the new value
        if (newData.filename) {
          queryClient.setQueryData(
            QueryKeys.document(newData.sessionId, newData.documentId),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (old: any) => {
              if (!old) return old;
              return {
                ...old,
                result: {
                  ...old.result,
                  filename: newData.filename
                }
              };
            }
          );
        }

        return { previousDocument };
      },
      onError: (error: unknown, newData, context) => {
        // Rollback optimistic update
        if (context?.previousDocument) {
          queryClient.setQueryData(
            QueryKeys.document(newData.sessionId, newData.documentId),
            context.previousDocument
          );
        }

        // Don't show toast for 401 errors - handled by auth context (redirect to login)
        if (error instanceof Error && 'isAxiosError' in error) {
          const axiosError = error as AxiosError<BaseError>;
          if (axiosError.response?.status === 401) {
            return; // Silently fail - user is being redirected to login
          }
        }

        // Handle ValidationError (from 422 responses)
        if (error instanceof ValidationError) {
          toast.error(error.message || 'Failed to save document');
          return;
        }

        // Handle AxiosError
        if (error instanceof Error && 'isAxiosError' in error) {
          const axiosError = error as AxiosError<BaseError>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const detail = (axiosError.response?.data as any)?.detail;
          // detail can be a string or an array of validation errors
          const detailMsg =
            typeof detail === 'string'
              ? detail
              : Array.isArray(detail)
                ? detail[0]?.msg || 'Validation error'
                : undefined;
          const msg =
            axiosError.response?.data?.error ||
            axiosError.response?.data?.message ||
            detailMsg ||
            'Failed to save document';
          toast.error(msg);

          // Document saves shouldn't 4xx — if they do we need the response
          // body to debug (which field pydantic rejected). The general 422
          // filter in main.tsx drops the raw axios error, so capture a
          // dedicated message with the body attached and a distinct tag so
          // it bypasses the status-based filters.
          const status = axiosError.response?.status;
          if (status && status >= 400 && status !== 401 && status !== 409) {
            Sentry.captureMessage('Document save rejected by backend', {
              level: 'error',
              tags: { endpoint: 'update-document', status: String(status) },
              extra: {
                status,
                sessionId: newData.sessionId,
                documentId: newData.documentId,
                responseBody: axiosError.response?.data,
                requestFilename: newData.filename
              }
            });
          }
          return;
        }

        // Fallback
        toast.error('Failed to save document');
      },
      onSuccess: async (response, request) => {
        // Document queries stay fresh indefinitely. Keep their body and
        // metadata current even when an editor skips refetching after Save.
        queryClient.setQueryData<DocumentResult | undefined>(
          QueryKeys.document(request.sessionId, request.documentId),
          cached => {
            if (!cached) return cached;
            return {
              ...cached,
              result: {
                ...cached.result,
                ...response.data?.result,
                // PUT returns metadata with a null document. Keep the body
                // we sent, or the existing body for a metadata-only update.
                document: request.document ?? cached.result.document
              }
            };
          }
        );
        await queryClient.invalidateQueries(
          QueryKeys.translationQuote(request.sessionId, request.documentId)
        );
        if (request?.dontInvalidateQueries) return;

        // Invalidate to ensure we have the latest data
        await queryClient.invalidateQueries(
          QueryKeys.document(request.sessionId, request.documentId)
        );

        await queryClient.invalidateQueries({
          queryKey: ['folders'],
          exact: false,
          refetchActive: true,
          refetchInactive: true
        });

        await queryClient.invalidateQueries([
          'transcript-editor-session',
          'folder',
          request.sessionId
        ]);
      }
    }
  );
}
